import Header from "@/components/Header";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Partners from "@/components/Partners";
import PropertySelector from "@/components/PropertySelector";
import PropertyMap from "@/components/PropertyMap";
import Contact from "@/components/Contact";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import NewsletterPopup from "@/components/NewsletterPopup";
import NewsAndPressCarousel from "@/components/NewsAndPressCarousel";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Building, MapPin, Cpu } from "lucide-react";
const Index = () => {
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const aboutAnimation = useScrollAnimation();
  const pillarsAnimation = useScrollAnimation();
  const partnersAnimation = useScrollAnimation();
  const contactAnimation = useScrollAnimation();
  useEffect(() => {
    // Check if user has already subscribed
    const hasSubscribed = localStorage.getItem('newsletter-subscribed');

    // Don't show if already subscribed
    if (hasSubscribed) return;

    // Check if popup was dismissed recently (within 12 hours)
    const dismissedTime = localStorage.getItem('newsletter-dismissed');
    if (dismissedTime) {
      const twelveHoursInMs = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime);
      
      // Don't show if dismissed within the last 12 hours
      if (timeSinceDismissed < twelveHoursInMs) return;
    }

    // Show popup after 3 seconds
    const timer = setTimeout(() => {
      setShowNewsletterPopup(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  return <div className="min-h-screen">
      <Header />
      <Hero />
      <section ref={aboutAnimation.ref} className={`scroll-fade-in min-h-[40vh] sm:min-h-[50vh] md:min-h-[60vh] lg:min-h-screen flex items-center ${aboutAnimation.isVisible ? 'visible' : ''}`}>
        <div className="w-full relative">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-muted/30 to-background/95 pointer-events-none"></div>
          {/* Shadow elements */}
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/50 to-transparent"></div>
          <About />
        </div>
      </section>
      
      {/* Our Pillars Section */}
      <section ref={pillarsAnimation.ref} className={`scroll-fade-in py-6 sm:py-8 md:py-16 lg:py-24 xl:py-32 bg-background ${pillarsAnimation.isVisible ? 'visible' : 'opacity-100'}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-6 sm:mb-8 md:mb-12 lg:mb-16 xl:mb-20">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-light text-black mb-3 sm:mb-4 md:mb-6 tracking-tight">
                Our Pillars
              </h2>
              <div className="w-12 sm:w-16 md:w-24 h-px bg-black mx-auto mb-3 sm:mb-4 md:mb-6 lg:mb-8"></div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-black/80 max-w-3xl mx-auto leading-relaxed">
                Four distinct verticals that define our comprehensive approach to hospitality and real estate excellence
              </p>
            </div>
            
            {/* Mobile-First Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 lg:grid-rows-8 gap-3 sm:gap-4 md:gap-6 auto-rows-fr md:h-auto lg:h-[700px]">
              {/* Homes - Top Left Large */}
              <Link 
                to="/portfolio/homes"
                className="sm:col-span-2 lg:col-span-7 lg:row-span-4 group relative bg-gradient-to-br from-black/5 to-black/10 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 hover:from-black/10 hover:to-black/20 transition-all duration-700 overflow-hidden min-h-[200px] sm:min-h-[240px] md:min-h-[280px] lg:min-h-0"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-black/5 to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="relative z-10 h-full flex flex-col justify-between">
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-5xl xl:text-6xl 2xl:text-8xl font-extralight text-black/10 group-hover:text-black/20 transition-colors duration-500">01</div>
                    <div className="space-y-1 sm:space-y-2 md:space-y-3">
                      <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-5xl font-light text-black">Homes</h3>
                      <div className="w-6 sm:w-8 md:w-12 lg:w-16 h-px bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                    </div>
                  </div>
                  <div className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-6">
                    <p className="text-xs sm:text-sm md:text-base lg:text-lg text-black/70 max-w-md leading-relaxed">Luxury serviced apartments and aparthotels designed for modern living experiences</p>
                    <div className="inline-flex items-center gap-2 text-black font-medium group-hover:gap-4 transition-all duration-300 text-xs sm:text-sm md:text-base">
                      Explore <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Hotels - Top Right */}
              <Link 
                to="/portfolio/hotels"
                className="sm:col-span-1 lg:col-span-5 lg:row-span-4 group relative bg-black text-white rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 hover:bg-black/90 transition-all duration-700 overflow-hidden min-h-[200px] sm:min-h-[240px] md:min-h-[280px] lg:min-h-0"
              >
                <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 bg-white/10 rounded-full transform translate-x-8 sm:translate-x-12 md:translate-x-16 -translate-y-8 sm:-translate-y-12 md:-translate-y-16 group-hover:scale-150 transition-transform duration-700"></div>
                <div className="relative z-10 h-full flex flex-col justify-between">
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-6xl font-extralight text-white/30 group-hover:text-white/50 transition-colors duration-500">02</div>
                    <div className="space-y-1 sm:space-y-2 md:space-y-3">
                      <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-light text-white">Hotels</h3>
                      <div className="w-6 sm:w-8 md:w-12 lg:w-16 h-px bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                    </div>
                  </div>
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    <p className="text-xs sm:text-sm md:text-base text-white/80 leading-relaxed">Premium hospitality experiences and operations</p>
                    <div className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-4 transition-all duration-300 text-xs sm:text-sm md:text-base">
                      Explore <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Developments - Bottom Left */}
              <Link 
                to="/portfolio/developments"
                className="sm:col-span-1 lg:col-span-5 lg:row-span-4 group relative bg-gradient-to-tl from-black/10 to-black/5 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 hover:from-black/20 hover:to-black/10 transition-all duration-700 overflow-hidden min-h-[200px] sm:min-h-[240px] md:min-h-[280px] lg:min-h-0"
              >
                <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-18 sm:h-18 md:w-24 md:h-24 bg-black/10 rounded-full transform -translate-x-4 sm:-translate-x-6 md:-translate-x-8 translate-y-4 sm:translate-y-6 md:translate-y-8 group-hover:scale-150 transition-transform duration-700"></div>
                <div className="relative z-10 h-full flex flex-col justify-between">
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-6xl font-extralight text-black/10 group-hover:text-black/20 transition-colors duration-500">03</div>
                    <div className="space-y-1 sm:space-y-2 md:space-y-3">
                      <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-light text-black">Developments</h3>
                      <div className="w-6 sm:w-8 md:w-12 lg:w-16 h-px bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                    </div>
                  </div>
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    <p className="text-xs sm:text-sm md:text-base text-black/70 leading-relaxed">Innovative real estate development projects</p>
                    <div className="inline-flex items-center gap-2 text-black font-medium group-hover:gap-4 transition-all duration-300 text-xs sm:text-sm md:text-base">
                      Explore <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Technologies - Bottom Right Large */}
              <Link 
                to="/portfolio/technologies"
                className="sm:col-span-2 lg:col-span-7 lg:row-span-4 group relative bg-gradient-to-tr from-black/5 via-black/10 to-black/15 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 hover:from-black/10 hover:via-black/15 hover:to-black/20 transition-all duration-700 overflow-hidden min-h-[200px] sm:min-h-[240px] md:min-h-[280px] lg:min-h-0"
              >
                <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(0,0,0,0.05)_25%,rgba(0,0,0,0.05)_50%,transparent_50%,transparent_75%,rgba(0,0,0,0.05)_75%)] bg-[length:20px_20px] opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="relative z-10 h-full flex flex-col justify-between">
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-5xl xl:text-6xl 2xl:text-8xl font-extralight text-black/10 group-hover:text-black/20 transition-colors duration-500">04</div>
                    <div className="space-y-1 sm:space-y-2 md:space-y-3">
                      <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-5xl font-light text-black">Technologies</h3>
                      <div className="w-6 sm:w-8 md:w-12 lg:w-16 h-px bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                    </div>
                  </div>
                  <div className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-6">
                    <p className="text-xs sm:text-sm md:text-base lg:text-lg text-black/70 max-w-md leading-relaxed">Cutting-edge digital transformation solutions driving innovation</p>
                    <div className="inline-flex items-center gap-2 text-black font-medium group-hover:gap-4 transition-all duration-300 text-xs sm:text-sm md:text-base">
                      Explore <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <section ref={partnersAnimation.ref} className={`scroll-fade-in min-h-[30vh] sm:min-h-[40vh] md:min-h-[50vh] lg:min-h-screen flex items-center ${partnersAnimation.isVisible ? 'visible' : ''}`}>
        <div className="w-full relative">
          <Partners />
        </div>
      </section>
      
      {/* News and Press Carousel */}
      <NewsAndPressCarousel />
      
      <section className="min-h-[20vh] sm:min-h-[25vh] md:min-h-[30vh] lg:min-h-[40vh] flex items-center">
        <div className="w-full relative">
          <FAQ />
        </div>
      </section>
      
      <section ref={contactAnimation.ref} className={`scroll-fade-in min-h-[25vh] sm:min-h-[30vh] md:min-h-[35vh] lg:min-h-[50vh] flex items-center ${contactAnimation.isVisible ? 'visible' : ''}`}>
        <div className="w-full relative">
          <Contact />
        </div>
      </section>
      <Footer />
      <ScrollToTop />
      <NewsletterPopup open={showNewsletterPopup} onOpenChange={setShowNewsletterPopup} />
    </div>;
};
export default Index;